import React, { useEffect, useMemo, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";
import { SlidersHorizontal, X, RotateCcw } from "lucide-react";

const catStyle = {
  "Strong Match": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Review Recommended": "bg-amber-100 text-amber-800 border-amber-200",
  "Exception Required": "bg-orange-100 text-orange-800 border-orange-200",
};

const EMPTY_FILTERS = {
  amount_min: "", amount_max: "",
  loan_type: "", district: "", sector: "",
  vintage_min: "", turnover_band: "", constitution: "",
  credit_band: "", collateral_offered: "", guarantee_pref: "",
  scheme_eligible: "", docs_complete: "", purpose: "",
  liabilities_max: "",
};

function pill(active) {
  return `rounded-md border px-3 py-2 text-sm ${active ? "border-[color:var(--bk-navy)] bg-[color:var(--bk-navy)] text-white" : "border-slate-300 bg-white hover:border-slate-400"}`;
}

export default function OpportunityMarketplace() {
  const pt = usePhrase();
  const [opps, setOpps] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t, tv, lang } = useLang();

  useEffect(() => { api.get("/lender/opportunities").then((r) => setOpps(r.data)); }, []);

  const distinct = (key) => [...new Set(opps.map((o) => o[key]).filter(Boolean))].sort();
  const districts = distinct("district");
  const sectors = distinct("sector");
  const loanTypes = distinct("loan_type");
  const constitutions = distinct("constitution");
  const purposes = distinct("purpose");
  const turnoverBands = distinct("turnover_band");
  const creditBands = distinct("credit_band");

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "" && v !== null && v !== undefined).length,
    [filters]
  );
  const reset = () => setFilters(EMPTY_FILTERS);

  const filtered = useMemo(() => opps.filter((o) => {
    const amount = Number(o.requested_amount || 0);
    if (filters.amount_min && amount < Number(filters.amount_min)) return false;
    if (filters.amount_max && amount > Number(filters.amount_max)) return false;
    if (filters.loan_type && o.loan_type !== filters.loan_type) return false;
    if (filters.district && o.district !== filters.district) return false;
    if (filters.sector && o.sector !== filters.sector) return false;
    if (filters.vintage_min && Number(o.vintage_years || 0) < Number(filters.vintage_min)) return false;
    if (filters.turnover_band && o.turnover_band !== filters.turnover_band) return false;
    if (filters.constitution && o.constitution !== filters.constitution) return false;
    if (filters.credit_band && o.credit_band !== filters.credit_band) return false;
    if (filters.collateral_offered === "yes" && !o.collateral_available) return false;
    if (filters.collateral_offered === "no" && o.collateral_available) return false;
    if (filters.guarantee_pref === "cgtmse" && !o.cgtmse_available) return false;
    if (filters.guarantee_pref === "no" && o.cgtmse_available) return false;
    if (filters.scheme_eligible === "yes" && !(o.scheme_eligibility_count > 0)) return false;
    if (filters.scheme_eligible === "no" && o.scheme_eligibility_count > 0) return false;
    if (filters.docs_complete === "yes" && !(o.document_completeness >= 80)) return false;
    if (filters.docs_complete === "no" && o.document_completeness >= 80) return false;
    if (filters.purpose && o.purpose !== filters.purpose) return false;
    if (filters.liabilities_max && Number(o.existing_liabilities || 0) > Number(filters.liabilities_max)) return false;
    return true;
  }), [opps, filters]);

  return (
    <div className="space-y-6" data-testid="opp-marketplace">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 brand-heading">{t("opportunity_marketplace")}</h1>
          <p className="text-slate-600 mt-1 text-sm">{t("om_sub")}</p>
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="opp-filters-open"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--bk-navy)] text-white px-4 py-2.5 text-sm font-semibold"
        >
          <SlidersHorizontal className="w-4 h-4" /> {t("om_filters")} {activeCount > 0 && <span className="ml-1 rounded-full bg-white/25 px-2 py-0.5 text-xs">{activeCount}</span>}
        </button>
      </div>

      <div className="text-xs text-slate-500" data-testid="opp-count">
        {lang === "hi" ? <>{opps.length} में से <strong className="text-slate-900">{filtered.length}</strong> अवसर दिखाए जा रहे हैं</> : <>Showing <strong className="text-slate-900">{filtered.length}</strong> of {opps.length} opportunities</>}
        {activeCount > 0 && <button onClick={reset} className="ml-3 inline-flex items-center gap-1 text-slate-900 font-semibold hover:text-orange-600" data-testid="opp-reset-filters"><RotateCcw className="w-3 h-3" /> {t("om_clear")}</button>}
      </div>

      <div className="overflow-x-auto table-scroll rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="py-3 px-4">{t("col_ref_short")}</th><th className="py-3 px-4">{t("col_district")}</th><th className="py-3 px-4">{t("col_sector")}</th><th className="py-3 px-4">{t("om_vintage")}</th>
              <th className="py-3 px-4">{t("om_turnover")}</th><th className="py-3 px-4">{t("col_requested")}</th><th className="py-3 px-4">{t("om_purpose")}</th>
              <th className="py-3 px-4">{t("col_readiness")}</th><th className="py-3 px-4">{t("col_match")}</th><th className="py-3 px-4">{t("col_category")}</th><th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`opp-row-${o.id}`}>
                <td className="py-3 px-4 font-mono text-xs">{o.id.slice(0, 8)}</td>
                <td className="py-3 px-4">{o.district}</td>
                <td className="py-3 px-4">{tv(o.sector)}</td>
                <td className="py-3 px-4">{o.vintage_years || "—"} {t("om_yr")}</td>
                <td className="py-3 px-4">{o.turnover_band}</td>
                <td className="py-3 px-4 font-semibold">{formatINR(o.requested_amount)}</td>
                <td className="py-3 px-4">{tv(o.purpose)}</td>
                <td className="py-3 px-4">{o.readiness_band ? tv(o.readiness_band) : "—"}</td>
                <td className="py-3 px-4 font-bold text-slate-900">{o.match_percentage}%</td>
                <td className="py-3 px-4"><span className={`rounded-full border text-xs px-2 py-0.5 ${catStyle[o.category] || "bg-slate-100 border-slate-200"}`}>{tv(o.category)}</span></td>
                <td className="py-3 px-4"><Link to={`/bank/opportunities/${o.id}`} className="text-xs font-semibold text-slate-900 hover:text-orange-600" data-testid={`opp-open-${o.id}`}>{t("open")}</Link></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-slate-500">{t("om_none")}</td></tr>}
          </tbody>
        </table>
      </div>

      <DisclaimerBanner compact />

      {/* Filter drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false); }}
          data-testid="opp-filters-drawer"
        >
          <aside className="w-full sm:max-w-md h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <div className="font-semibold text-[color:var(--bk-navy)] brand-heading">{pt("Filter opportunities")}</div>
                <div className="text-[11px] text-slate-500">{activeCount} filter{activeCount === 1 ? "" : "s"} applied</div>
              </div>
              <button onClick={() => setDrawerOpen(false)} data-testid="opp-filters-close" className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5 text-sm">
              <Section label={pt("Loan amount (₹)")}>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" placeholder={pt("Min")} value={filters.amount_min} onChange={(e) => set("amount_min", e.target.value)} data-testid="f-amount-min" className="rounded-md border border-slate-300 px-3 py-2" />
                  <input type="number" placeholder={pt("Max")} value={filters.amount_max} onChange={(e) => set("amount_max", e.target.value)} data-testid="f-amount-max" className="rounded-md border border-slate-300 px-3 py-2" />
                </div>
              </Section>
              <Select label={pt("Loan type")} value={filters.loan_type} onChange={(v) => set("loan_type", v)} options={loanTypes} testId="f-loan-type" />
              <Select label={pt("Geography (district)")} value={filters.district} onChange={(v) => set("district", v)} options={districts} testId="f-district" />
              <Select label={pt("Industry / sector")} value={filters.sector} onChange={(v) => set("sector", v)} options={sectors} testId="f-sector" />
              <Section label={pt("Business vintage (years, minimum)")}>
                <input type="number" min="0" value={filters.vintage_min} onChange={(e) => set("vintage_min", e.target.value)} data-testid="f-vintage-min" className="w-full rounded-md border border-slate-300 px-3 py-2" />
              </Section>
              <Select label={pt("Turnover band")} value={filters.turnover_band} onChange={(v) => set("turnover_band", v)} options={turnoverBands} testId="f-turnover" />
              <Select label={pt("Constitution")} value={filters.constitution} onChange={(v) => set("constitution", v)} options={constitutions} testId="f-constitution" />
              <Select label={pt("Credit profile band")} value={filters.credit_band} onChange={(v) => set("credit_band", v)} options={creditBands} testId="f-credit" />
              <Section label={pt("Existing liabilities (max ₹)")}>
                <input type="number" placeholder={pt("No cap")} value={filters.liabilities_max} onChange={(e) => set("liabilities_max", e.target.value)} data-testid="f-liab-max" className="w-full rounded-md border border-slate-300 px-3 py-2" />
              </Section>
              <Toggle label={pt("Collateral available")} value={filters.collateral_offered} onChange={(v) => set("collateral_offered", v)} testId="f-collateral" />
              <Toggle label={pt("Guarantee preference — CGTMSE")} value={filters.guarantee_pref} onChange={(v) => set("guarantee_pref", v)} valueYes="cgtmse" testId="f-guarantee" />
              <Toggle label={pt("Scheme eligibility present")} value={filters.scheme_eligible} onChange={(v) => set("scheme_eligible", v)} testId="f-scheme" />
              <Toggle label={pt("Documents complete (≥80%)")} value={filters.docs_complete} onChange={(v) => set("docs_complete", v)} testId="f-docs" />
              <Select label={pt("Funding purpose")} value={filters.purpose} onChange={(v) => set("purpose", v)} options={purposes} testId="f-purpose" />
            </div>
            <div className="border-t border-slate-200 p-4 flex items-center justify-between gap-3">
              <button onClick={reset} data-testid="opp-filters-reset" className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white text-slate-800 px-4 py-2 text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> {pt("Reset")}</button>
              <button onClick={() => setDrawerOpen(false)} data-testid="opp-filters-apply" className="rounded-full bg-[color:var(--bk-navy)] text-white px-5 py-2 text-xs font-semibold">{pt(`Show ${filtered.length} results`)}</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  const pt = usePhrase();
  return (
    <Section label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
        <option value="">{pt("Any")}</option>
        {options.map((o) => <option key={o} value={o}>{pt(o)}</option>)}
      </select>
    </Section>
  );
}

function Toggle({ label, value, onChange, valueYes = "yes", testId }) {
  const pt = usePhrase();
  return (
    <Section label={label}>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange("")} data-testid={`${testId}-any`} className={pill(value === "")}>{pt("Any")}</button>
        <button type="button" onClick={() => onChange(valueYes)} data-testid={`${testId}-yes`} className={pill(value === valueYes)}>{pt("Yes")}</button>
        <button type="button" onClick={() => onChange("no")} data-testid={`${testId}-no`} className={pill(value === "no")}>{pt("No")}</button>
      </div>
    </Section>
  );
}
